// AI 生图 API 服务
import type {
  Conversation,
  Message,
  EffectTemplate,
  GenerateRequest,
  GenerateResponse,
  TaskStatusResponse,
  ApiResponse,
  ModelConfig,
} from '../types/aiGenerate';

const getUid = () => localStorage.getItem('fdd_uid') || '';

// 获取对话列表
export async function getConversations(): Promise<Conversation[]> {
  const response = await fetch(`/ai/conversations?uid=${getUid()}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<Conversation[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取对话列表失败');
}

// 创建新对话
export async function createConversation(title?: string): Promise<Conversation> {
  const response = await fetch('/ai/conversations', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title: title || '新对话', uid: getUid() }),
  });
  const result: ApiResponse<Conversation> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '创建对话失败');
}

// 更新对话（重命名）
export async function updateConversation(id: string, title: string): Promise<void> {
  const response = await fetch(`/ai/conversations/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title, uid: getUid() }),
  });
  const result: ApiResponse<void> = await response.json();
  if (!result.success) {
    throw new Error(result.message || '更新对话失败');
  }
}

// 删除对话
export async function deleteConversation(id: string): Promise<void> {
  const response = await fetch(`/ai/conversations/${id}?uid=${getUid()}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<void> = await response.json();
  if (!result.success) {
    throw new Error(result.message || '删除对话失败');
  }
}

// 获取对话消息
export async function getMessages(conversationId: string): Promise<Message[]> {
  const response = await fetch(
    `/ai/conversations/${conversationId}/messages?uid=${getUid()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );
  const result: ApiResponse<Message[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取消息失败');
}

// 发起生成请求
export async function generateImage(request: GenerateRequest): Promise<GenerateResponse> {
  const response = await fetch('/ai/generate', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...request, uid: getUid() }),
  });
  const result: ApiResponse<GenerateResponse> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '生成请求失败');
}

// 查询生成状态
export async function getTaskStatus(taskId: string): Promise<TaskStatusResponse> {
  const response = await fetch(`/ai/generate/${taskId}/status?uid=${getUid()}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<TaskStatusResponse> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '查询状态失败');
}

// 获取效果模板列表
export async function getEffectTemplates(): Promise<EffectTemplate[]> {
  const response = await fetch('/ai/templates', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<EffectTemplate[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取模板失败');
}

// 获取可用模型列表
export async function getModels(): Promise<ModelConfig[]> {
  const response = await fetch('/ai/models', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<ModelConfig[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  // 如果接口失败，返回默认模型
  return [
    { id: 'jimeng-4.0', name: '即梦 4.0' },
  ];
}

// 默认生成参数
export const DEFAULT_GENERATE_PARAMS = {
  model: 'jimeng-4.0',
  count: 1,
  aspectRatio: '1:1',
  resolution: '1K',
  continuousMode: false,
};

// 尺寸选项
export const ASPECT_RATIO_OPTIONS = [
  { value: '1:1', label: '1:1', width: 1, height: 1 },
  { value: '4:3', label: '4:3', width: 4, height: 3 },
  { value: '3:4', label: '3:4', width: 3, height: 4 },
  { value: '16:9', label: '16:9', width: 16, height: 9 },
  { value: '9:16', label: '9:16', width: 9, height: 16 },
];

// 分辨率选项
export const RESOLUTION_OPTIONS = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

// 生成数量选项
export const COUNT_OPTIONS = [1, 2, 3, 4];
